import { brandRoutes, guestRoutes } from "~/ui-rtk/constants/routes"
import { SessionRole } from "~/api/dto/auth/session-role"

import type { TSidebarItemProps } from "./SidebarItem"
import { POSTHOG_FEATURES } from "~/ui-rtk/constants/feature-flags"
import { URL_SETTINGS_PARAM_KEY } from "~/ui-rtk/constants/url-params"

import { HomeIcon } from "../../ui/common/Icons/HomeIcon"
import { CompassIcon } from "../../ui/common/Icons/CompassIcon"
import { RocketIcon } from "../../ui/common/Icons/RocketIcon"
import { FlameIcon } from "../../ui/common/Icons/FlameIcon"
import { CircleDollarSignIcon } from "../../ui/common/Icons/CircleDolarIcon"
import { BadgePercentIcon } from "../../ui/common/Icons/BadgePercentIcon"
import { SettingsGearIcon } from "../../ui/common/Icons/SettingsGearIcon"
import {
  TAnimatedSvgIconProps,
  TAnimatedSvgIconSize,
} from "../../ui/common/Icons/IconType"
import { ForwardRefComponent } from "motion/react"
import { FingerprintIcon } from "../../ui/common/Icons/FingerPrintIcon"
import { ShoppingCartIcon } from "../../ui/common/Icons/ShoppingCartIcon"
import { ChartBarIcon } from "../../ui/common/Icons/ChartBarIcon"
import { MessageSquareIcon } from "../../ui/common/Icons/MessageSquareIcon"

export const ALL_ROLES = [
  SessionRole.SUPER_ADMIN,
  SessionRole.ADMIN,
  SessionRole.USER,
  SessionRole.ADMIN_WITH_ACCESS,
  SessionRole.USER_WITH_ACCESS,
]

export const sidebarItems = ({
  isGAConnectorConnected,
  isShopifyEnabled,
  isMediaMentionsEnabled,
  isCreativeReportsEnabled,
  isBrandPositioningEnabled,
  isPaidMediaWeightsEnabled,
  isExperimentsEnabled,
  isBudgetManagementEnabled,
  isWebMerchandisingEnabled,
}: {
  isGAConnectorConnected: boolean
  isShopifyEnabled: boolean
  isMediaMentionsEnabled: boolean
  isCreativeReportsEnabled: boolean
  isBrandPositioningEnabled: boolean
  isPaidMediaWeightsEnabled: boolean
  isExperimentsEnabled: boolean
  isBudgetManagementEnabled: boolean
  isWebMerchandisingEnabled: boolean
}): Pick<
  TSidebarItemProps,
  | "Icon"
  | "label"
  | "path"
  | "items"
  | "availableTo"
  | "isHidden"
  | "featureFlag"
>[] =>
  [
    // Home - Overview, KPI Deep Dive, Metrics Explorer
    {
      path: brandRoutes.HOME.OVERVIEW,
      label: "Home",
      Icon: HomeIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      availableTo: ALL_ROLES,
      items: [
        {
          path: brandRoutes.HOME.OVERVIEW,
          label: "Overview",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.HOME.KPI_DEEP_DIVE,
          label: "KPI Deep Dive",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.HOME.METRICS_EXPLORER,
          label: "Metrics Explorer",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.HOME.LTV_COHORTS,
          label: "LTV Cohorts",
          isHidden: () => !isShopifyEnabled,
          featureFlag: POSTHOG_FEATURES.LTV_COHORTS,
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.HOME.SUBSEQUENT_PURCHASES,
          label: "Subsequent Purchases",
          isHidden: () => !isShopifyEnabled,
          featureFlag: POSTHOG_FEATURES.SUBSEQUENT_PURCHASES,
          availableTo: ALL_ROLES,
        },
      ],
    },
    // Paid Media - Paid Media Optimization, Creative Reporting, Paid Media Weights (feature flag)
    {
      path: brandRoutes.BRAND_MEDIA.PAID_OPTIMIZATION,
      label: "Paid Media",
      availableTo: ALL_ROLES,
      Icon: RocketIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      items: [
        {
          path: brandRoutes.BRAND_MEDIA.PAID_OPTIMIZATION,
          label: "Paid Media Optimization",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.CREATIVE_REPORTS.ROOT,
          label: "Creative Reporting",
          isHidden: () => !isCreativeReportsEnabled,
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.CREATIVE_REPORTS.TAXONOMY,
          label: "Creative Taxonomy",
          isHidden: () => !isCreativeReportsEnabled,
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.BRAND_MEDIA.AD_DESTINATIONS,
          label: "Ad Destinations",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.BRAND_MEDIA.PAID_WEIGHTS,
          label: "Paid Media Weights",
          isHidden: () => !isPaidMediaWeightsEnabled,
          availableTo: ALL_ROLES,
        },
      ],
    },
    // Web Merchandising - keep same sub nav structure
    {
      path: brandRoutes.WEB_MERCHANDISING.COLLECTION_FUNNEL,
      label: "Web Merchandising",
      isHidden: () => !isWebMerchandisingEnabled,
      availableTo: ALL_ROLES,
      Icon: ShoppingCartIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      items: [
        {
          path: brandRoutes.WEB_MERCHANDISING.COLLECTION_FUNNEL,
          label: "Collection Page Performance",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.WEB_MERCHANDISING.PRODUCT_PAGE_FUNNEL,
          label: "Product Page Performance",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.WEB_MERCHANDISING.HOMEPAGE_FUNNEL,
          label: "Homepage Funnel",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.WEB_MERCHANDISING.PAGE_PATH_REPORT,
          label: "Page Path Report",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.WEB_MERCHANDISING.LANDING_PAGE_REPORT,
          label: "Landing Page Report",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.WEB_MERCHANDISING.PRODUCT_MEDIA_PNL,
          label: "Product Media P&L",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.WEB_MERCHANDISING.PRE_ATC_ANALYSIS,
          label: "Pre ATC Analysis",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.WEB_MERCHANDISING.PAGE_DEPTH_ANALYSIS,
          label: "Page Depth Analysis",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.WEB_ANALYTICS.JOURNEY_ANALYSIS,
          label: "Path Exploration",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.WEB_MERCHANDISING.INVENTORY_ANALYSIS,
          label: "Inventory Analysis",
          availableTo: ALL_ROLES,
        },
      ],
    },
    // Organic Media - Organic Social Posts, Media Mentions
    {
      path: brandRoutes.ORGANIC_MEDIA.ORGANIC_SOCIAL_POSTS,
      label: "Organic Media",
      availableTo: ALL_ROLES,
      Icon: CompassIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      items: [
        {
          path: brandRoutes.ORGANIC_MEDIA.ORGANIC_SOCIAL_POSTS,
          label: "Organic Social Posts",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.ORGANIC_MEDIA.MEDIA_MENTIONS,
          label: "Media Mentions",
          isHidden: () => !isMediaMentionsEnabled,
          availableTo: ALL_ROLES,
        },
      ],
    },
    // Brand Research - Market Share (share of search), Brand Positioning, Mentions Analysis
    {
      path: brandRoutes.MARKET_SHARE.SHARE_OF_SEARCH,
      label: "Brand Research",
      availableTo: ALL_ROLES,
      Icon: BadgePercentIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      items: [
        {
          path: brandRoutes.MARKET_SHARE.SHARE_OF_SEARCH,
          label: "Market Share",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.BRAND_POSITIONING.ROOT,
          label: "Brand Positioning",
          isHidden: () => !isBrandPositioningEnabled,
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.BRAND_RESEARCH.MENTIONS_ANALYSIS,
          label: "Mentions Analysis",
          availableTo: ALL_ROLES,
        },
      ],
    },
    // Revenue (feature flag) - Google Analytics, Shopify
    {
      path: brandRoutes.REVENUE.GOOGLE_ANALYTICS,
      label: "Revenue",
      availableTo: ALL_ROLES,
      isHidden: () => !isGAConnectorConnected && !isShopifyEnabled,
      Icon: CircleDollarSignIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      items: [
        {
          path: brandRoutes.REVENUE.GOOGLE_ANALYTICS,
          label: "Google Analytics",
          isHidden: () => !isGAConnectorConnected,
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.REVENUE.SHOPIFY,
          label: "Shopify",
          isHidden: () => !isShopifyEnabled,
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.REVENUE.SHOPIFY_ORDERS,
          label: "Shopify Orders",
          isHidden: () => !isShopifyEnabled,
          availableTo: ALL_ROLES,
        },
      ],
    },
    // Experiments (feature flag)
    {
      path: brandRoutes.EXPERIMENTS.ROOT,
      label: "Experiments",
      isHidden: () => !isExperimentsEnabled,
      availableTo: ALL_ROLES,
      Icon: FlameIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
    },
    // Campaign Management (feature flag)
    {
      path: brandRoutes.CAMPAIGN_MANAGEMENT.ROOT,
      label: "Campaign Management",
      isHidden: () => !isBudgetManagementEnabled,
      availableTo: ALL_ROLES,
      Icon: FingerprintIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
    },
    // Campaign Blaster - Multi-step Meta Ads campaign creation
    {
      path: brandRoutes.CAMPAIGN_BLASTER.ROOT,
      label: "Campaign Blaster",
      availableTo: ALL_ROLES,
      Icon: RocketIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
    },
    // Ad Creation — groups the creative-production tools under one top-level
    // entry: Ad Forge (creative generation), Ad Blaster (library of saved
    // creatives + launched ads with bulk re-blast), Creative Management
    // (Meta ad archival playbooks), Brand Agenda (planning calendar of
    // scheduled blasts/creatives/budget work), and Jobs (background
    // operations like ad blasts). Gated by the Ad Forge feature flag — users
    // without Ad Forge have nothing to create or list here.
    {
      path: brandRoutes.AD_BLASTER.AD_FORGE,
      label: "Ad Creation",
      availableTo: ALL_ROLES,
      featureFlag: POSTHOG_FEATURES.AD_FORGE_ENABLED,
      Icon: RocketIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      items: [
        {
          // Canonical AdForge route lives under AdBlaster; `/ad-forge` is only a
          // legacy redirect, so link the real path for correct active-state.
          path: brandRoutes.AD_BLASTER.AD_FORGE,
          label: "Ad Forge",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.AD_BLASTER.ROOT,
          label: "Ad Blaster",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.CREATIVE_MANAGEMENT.ROOT,
          label: "Creative Management",
          availableTo: ALL_ROLES,
          featureFlag: POSTHOG_FEATURES.CREATIVE_MANAGEMENT,
        },
        {
          path: brandRoutes.BRAND_AGENDA.ROOT,
          label: "Brand Agenda",
          availableTo: ALL_ROLES,
        },
        {
          path: brandRoutes.JOBS.ROOT,
          label: "Jobs",
          availableTo: ALL_ROLES,
        },
        {
          // Cross-company cron management — super admins only (the route is
          // also guarded in Main, this just hides the entry for everyone else).
          path: brandRoutes.ADMIN_SCHEDULED_JOBS.ROOT,
          label: "Scheduled Jobs",
          availableTo: [SessionRole.SUPER_ADMIN],
        },
      ],
    },
    // Market Prediction
    {
      path: brandRoutes.MARKET_PREDICTION.ROOT,
      label: "Market Prediction",
      availableTo: ALL_ROLES,
      Icon: ChartBarIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      featureFlag: POSTHOG_FEATURES.MARKET_PREDICTION,
    },
    // Comment Moderation (MD-5576): the daily read of the comments under the
    // brand's paid Meta posts — reactions, what was flagged or hidden, unhide.
    // Top-level, behind its own flag so it rolls out brand by brand; the
    // route in Main reads the same flag.
    {
      path: brandRoutes.COMMENT_MODERATION.ROOT,
      label: "Comment Moderation",
      availableTo: ALL_ROLES,
      Icon: MessageSquareIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      featureFlag: POSTHOG_FEATURES.COMMENT_MODERATION,
    },
    // Meta Budgets: list the ad account's campaigns live from the Marketing
    // API and edit campaign / ad set budgets. Same flag as the route in Main.
    {
      path: brandRoutes.META_BUDGETS.ROOT,
      label: "Meta Budgets",
      availableTo: ALL_ROLES,
      Icon: CircleDollarSignIcon as ForwardRefComponent<
        TAnimatedSvgIconProps,
        TAnimatedSvgIconSize
      >,
      featureFlag: POSTHOG_FEATURES.META_BUDGET_EDITOR,
    },
  ] as const

// Free Share of Search companies (APP-493) get this minimal nav instead of
// the product nav: their report, the keywords/competitors editing surface
// (the report's settings dialog, deep-linked via ?settings=…), the upsell
// page, and account settings. Everything else is hidden — deep links to paid
// routes still hit SubscriptionGuard's paywall.
export const sosFreeSidebarItems: Pick<
  TSidebarItemProps,
  "Icon" | "label" | "path" | "availableTo"
>[] = [
  {
    path: brandRoutes.MARKET_SHARE.SHARE_OF_SEARCH,
    label: "Share of Search",
    availableTo: ALL_ROLES,
    Icon: BadgePercentIcon as ForwardRefComponent<
      TAnimatedSvgIconProps,
      TAnimatedSvgIconSize
    >,
  },
  {
    path: `${brandRoutes.MARKET_SHARE.SHARE_OF_SEARCH}?${URL_SETTINGS_PARAM_KEY}=keywords`,
    label: "Keywords & Competitors",
    availableTo: ALL_ROLES,
    Icon: CompassIcon as ForwardRefComponent<
      TAnimatedSvgIconProps,
      TAnimatedSvgIconSize
    >,
  },
  {
    path: guestRoutes.UPGRADE,
    label: "More from Marathon",
    availableTo: ALL_ROLES,
    Icon: RocketIcon as ForwardRefComponent<
      TAnimatedSvgIconProps,
      TAnimatedSvgIconSize
    >,
  },
  {
    path: brandRoutes.SETTINGS.ROOT,
    label: "Settings",
    availableTo: ALL_ROLES,
    Icon: SettingsGearIcon as ForwardRefComponent<
      TAnimatedSvgIconProps,
      TAnimatedSvgIconSize
    >,
  },
]

export type TSetupSidebarItem = Pick<
  TSidebarItemProps,
  "Icon" | "label" | "path" | "items" | "availableTo" | "featureFlag"
>

export const setupSidebarItems: TSetupSidebarItem[] = [
  {
    path: brandRoutes.SETUP.ROOT,
    label: "Setup",
    availableTo: [
      SessionRole.SUPER_ADMIN,
      SessionRole.ADMIN,
      SessionRole.USER,
      SessionRole.ADMIN_WITH_ACCESS,
      SessionRole.USER_WITH_ACCESS,
    ],
    Icon: SettingsGearIcon as ForwardRefComponent<
      TAnimatedSvgIconProps,
      TAnimatedSvgIconSize
    >,
    items: [
      {
        path: brandRoutes.SETUP.KEYWORDS,
        label: "Keywords + Competitors",
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
      {
        path: brandRoutes.SETUP.CONNECTORS,
        label: "Connectors",
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
      {
        path: brandRoutes.SETUP.AUDIENCES,
        label: "Audiences",
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
      {
        path: brandRoutes.SETUP.FILE_DROP,
        label: "File Drop",
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
      {
        path: brandRoutes.SETUP.REVENUE_SOURCE,
        label: "Revenue Source",
        featureFlag: POSTHOG_FEATURES.REVENUE_DEP_VAR_SETUP_PAGE,
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
      {
        path: brandRoutes.SETUP.FINANCIAL_SETTINGS,
        label: "Financial Settings",
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
      {
        path: brandRoutes.SETUP.TAGS,
        label: "Tags",
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
      {
        path: brandRoutes.SETUP.METRIC_DICTIONARY,
        label: "Metric Dictionary",
        featureFlag: POSTHOG_FEATURES.METRIC_DICTIONARY,
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
    ],
  },
  {
    path: brandRoutes.AUDITS.ROOT,
    label: "Audits",
    availableTo: [
      SessionRole.SUPER_ADMIN,
      SessionRole.ADMIN,
      SessionRole.USER,
      SessionRole.ADMIN_WITH_ACCESS,
      SessionRole.USER_WITH_ACCESS,
    ],
    Icon: FingerprintIcon as ForwardRefComponent<
      TAnimatedSvgIconProps,
      TAnimatedSvgIconSize
    >,
    items: [
      {
        path: brandRoutes.AUDITS.DESTINATION_URLS,
        label: "Destination URLs",
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
      {
        path: brandRoutes.AUDITS.UTM,
        label: "UTM Audit",
        availableTo: [
          SessionRole.SUPER_ADMIN,
          SessionRole.ADMIN,
          SessionRole.USER,
          SessionRole.ADMIN_WITH_ACCESS,
          SessionRole.USER_WITH_ACCESS,
        ],
      },
      {
        path: brandRoutes.AUDITS.EXCLUSIONS,
        label: "Exclusions Audit",
        availableTo: ALL_ROLES,
      },
      {
        // Cross-company launch tracker over the ad-blast audit history —
        // super admins only (route is guarded in Main as well). Lives under
        // Audits rather than Ad Creation so it isn't hidden by the AdForge
        // feature flag.
        path: brandRoutes.ADMIN_AD_BLAST_REPORTS.ROOT,
        label: "Ad Blast Reports",
        availableTo: [SessionRole.SUPER_ADMIN],
      },
    ],
  },
] as const
